import React, { useState, useEffect, useMemo } from 'react';
import { AiOutlineClose, AiOutlineHome } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import { createAddress, fetchAddresses, updateAddress, deleteAddress, Address } from '../../store/slices/addressSlice';
import { AppDispatch, RootState } from '../../store';
import { toast } from '../../utils/toast';

interface AddressModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const AddressModal = ({ isOpen, onClose }: AddressModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { addresses } = useSelector((state: RootState) => state.address);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    // Form fields
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [pincode, setPincode] = useState('');
    const [locality, setLocality] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [landmark, setLandmark] = useState('');
    const [addressType, setAddressType] = useState('Home');

    const getLoggedInUserId = () => {
        if (typeof window === 'undefined') return '';
        const directId = localStorage.getItem('logged_in_id') ||
            localStorage.getItem('logged_in__id') ||
            localStorage.getItem('logged_in_user_id') ||
            localStorage.getItem('user_id');
        if (directId) return directId;
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const userObj = JSON.parse(userStr);
                if (userObj && userObj._id) return userObj._id;
            }
        } catch (e) { }
        return '';
    };

    const filteredAddresses = useMemo(() => {
        const userId = getLoggedInUserId();
        if (!userId) return [];
        return (addresses || []).filter(addr => {
            const addrUserId = addr.user_id || addr.userInfo?._id;
            return String(addrUserId) === String(userId);
        });
    }, [addresses]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setErrorMsg('');
            dispatch(fetchAddresses(undefined));
        } else {
            document.body.style.overflow = 'unset';
            setEditingAddressId(null);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, dispatch]);

    useEffect(() => {
        if (isOpen) {
            if (!filteredAddresses || filteredAddresses.length === 0) {
                setShowAddForm(true);
            } else {
                setShowAddForm(false);
            }
        }
    }, [isOpen, filteredAddresses]);

    if (!isOpen) return null;

    const handleBackToSaved = () => {
        setEditingAddressId(null);
        setName('');
        setPhone('');
        setPincode('');
        setLocality('');
        setAddress('');
        setCity('');
        setState('');
        setLandmark('');
        setAddressType('Home');
        setShowAddForm(false);
    };

    const handleEditClick = (addr: Address) => {
        setName(addr.name);
        setPhone(addr.shipping_phone);
        setPincode(addr.pincode);
        setLocality(addr.locality);
        setAddress(addr.address);
        setCity(addr.city);
        setState(addr.state);
        setLandmark(addr.landmark || '');
        setAddressType(addr.type ? (addr.type.charAt(0).toUpperCase() + addr.type.slice(1)) : 'Home');
        setEditingAddressId(addr._id || null);
        setShowAddForm(true);
    };

    const handleDeleteClick = (id?: string) => {
        if (!id) return;
        setConfirmDeleteId(id);
    };

    const confirmDeleteAddress = async (id: string) => {
        try {
            const result = await dispatch(deleteAddress(id));
            if (deleteAddress.fulfilled.match(result)) {
                dispatch(fetchAddresses(undefined));
                toast.success("Address deleted successfully!");
            } else {
                toast.error("Failed to delete address.");
            }
        } catch (err: any) {
            console.error("Error deleting address:", err);
            toast.error(err.message || "Failed to delete address.");
        } finally {
            setConfirmDeleteId(null);
        }
    };

    const handleSetDefault = async (addr: Address) => {
        if (!addr._id) return;
        try {
            const resultAction = await dispatch(updateAddress({
                id: addr._id,
                data: { ...addr, isDefault: true }
            }));
            if (updateAddress.fulfilled.match(resultAction)) {
                dispatch(fetchAddresses(undefined));
                toast.success("Address set as default successfully!");
            } else {
                toast.error('Failed to set default address.');
            }
        } catch (err: any) {
            console.error("Error setting default address:", err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMsg('');

        if (!name.trim() || !phone.trim() || !pincode.trim() || !locality.trim() || !city.trim() || !state.trim()) {
            setErrorMsg('All marked fields (*) are required.');
            return;
        }

        if (phone.length !== 10 || isNaN(Number(phone))) {
            setErrorMsg('Please enter a valid 10-digit shipping phone number.');
            return;
        }

        if (pincode.length !== 6 || isNaN(Number(pincode))) {
            setErrorMsg('Please enter a valid 6-digit pincode.');
            return;
        }

        setSubmitting(true);
        try {
            const userId = getLoggedInUserId();
            const isFirst = !filteredAddresses || filteredAddresses.length === 0;
            const newAddress: Address = {
                name: name.trim(),
                shipping_phone: phone.trim(),
                pincode: pincode.trim(),
                locality: locality.trim(),
                address: address.trim(),
                city: city.trim(),
                state: state.trim(),
                landmark: landmark.trim() || undefined,
                type: addressType.toLowerCase(),
                isDefault: isFirst || filteredAddresses.length === 0,
                ...(userId ? { user_id: userId } : {})
            };

            let resultAction;
            if (editingAddressId) {
                const existing = filteredAddresses.find(a => a._id === editingAddressId);
                newAddress.isDefault = existing ? existing.isDefault : false;
                resultAction = await dispatch(updateAddress({
                    id: editingAddressId,
                    data: newAddress
                }));
            } else {
                resultAction = await dispatch(createAddress(newAddress));
            }

            if (createAddress.fulfilled.match(resultAction) || updateAddress.fulfilled.match(resultAction)) {
                dispatch(fetchAddresses(undefined)); // Refresh addresses
                toast.success(editingAddressId ? 'Address updated successfully!' : 'Address added successfully!');
                setEditingAddressId(null);
                setShowAddForm(false);
                onClose();
            } else {
                setErrorMsg(resultAction.payload as string || 'Failed to save address. Please try again.');
            }
        } catch (err: any) {
            setErrorMsg(err.message || 'Error occurred while saving address.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="address-modal-overlay" onClick={onClose}>
            <style>
                {`
                .address-modal-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    display: flex;
                    justify-content: flex-end;
                    background-color: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    animation: fadeInOverlay 0.3s ease;
                }

                @keyframes fadeInOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideInRight {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }

                .address-drawer {
                    animation: slideInRight 0.45s cubic-bezier(0.16, 1, 0.3, 1);
                    width: 32% !important;
                    min-width: 440px !important;
                    background-color: #ffffff;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    box-shadow: -15px 0 45px rgba(15, 23, 42, 0.08);
                    border-top-left-radius: 24px;
                    border-bottom-left-radius: 24px;
                    overflow: hidden;
                }

                .address-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 24px;
                    border-bottom: 1px solid #f3f4f6;
                    background-color: #ffffff;
                }

                .address-title-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .address-title {
                    margin: 0;
                    font-size: 19px;
                    font-weight: 800;
                    color: #1f2937;
                    letter-spacing: -0.5px;
                }

                .address-close-btn {
                    background: #f3f4f6;
                    border: none;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #4b5563;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .address-close-btn:hover {
                    background-color: #e5e7eb;
                    color: #111827;
                    transform: scale(1.05);
                }

                .address-content {
                    flex: 1;
                    overflow-y: auto;
                    background-color: #fcfcfc;
                    padding: 24px;
                }

                .address-card {
                    padding: 18px;
                    background-color: #ffffff;
                    border: 1px solid #f3f4f6;
                    border-radius: 16px;
                    margin-bottom: 16px;
                    position: relative;
                    transition: all 0.25s ease;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.01);
                }

                .address-card:hover {
                    box-shadow: 0 8px 20px rgba(67, 200, 202, 0.06);
                    border-color: #43c8ca;
                }

                .address-card.default-address {
                    border: 2px solid #43c8ca;
                    background-color: #f0fbfb;
                }

                .address-badge {
                    font-size: 10px;
                    font-weight: 800;
                    padding: 3px 8px;
                    border-radius: 20px;
                    background: #f3f4f6;
                    color: #4b5563;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .address-badge.default {
                    background: #e6f9fa;
                    color: #14958f;
                }

                .address-actions-footer {
                    display: flex;
                    gap: 16px;
                    margin-top: 16px;
                    border-top: 1px solid #f3f4f6;
                    padding-top: 12px;
                    align-items: center;
                }

                .address-action-link {
                    background: none;
                    border: none;
                    color: #6b7280;
                    font-size: 11.5px;
                    font-weight: 700;
                    cursor: pointer;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 0;
                    transition: color 0.2s;
                }

                .address-action-link:hover {
                    color: #1f2937;
                }

                .address-action-link.teal {
                    color: #43c8ca;
                }

                .address-action-link.teal:hover {
                    color: #35a5a7;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    text-align: left;
                }

                .form-label {
                    font-size: 11.5px;
                    font-weight: 800;
                    color: #4b5563;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .form-input {
                    width: 100%;
                    padding: 12px 16px;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    outline: none;
                    font-size: 14px;
                    color: #1f2937;
                    background-color: #f9fafb;
                    transition: all 0.2s;
                    box-sizing: border-box;
                }

                .form-input:focus {
                    border-color: #43c8ca;
                    background-color: #ffffff;
                    box-shadow: 0 0 0 3px rgba(67, 200, 202, 0.15);
                }

                .type-btn {
                    padding: 8px 18px;
                    border: 1px solid #e5e7eb;
                    background: #ffffff;
                    color: #4b5563;
                    font-size: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                    border-radius: 10px;
                }

                .type-btn:hover {
                    background-color: #f9fafb;
                }

                .type-btn.active {
                    border-color: #43c8ca;
                    background: #f0fbfb;
                    color: #43c8ca;
                }

                .primary-action-btn {
                    width: 100%;
                    padding: 14px;
                    background-color: #43c8ca;
                    color: #ffffff;
                    font-weight: 800;
                    border: none;
                    border-radius: 12px;
                    cursor: pointer;
                    font-size: 13.5px;
                    letter-spacing: 0.8px;
                    text-transform: uppercase;
                    box-shadow: 0 4px 12px rgba(67, 200, 202, 0.25);
                    transition: all 0.2s;
                }

                .primary-action-btn:hover:not(:disabled) {
                    background-color: #3db0b2;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px rgba(67, 200, 202, 0.35);
                }

                .primary-action-btn:disabled {
                    background-color: #d1d5db;
                    color: #9ca3af;
                    box-shadow: none;
                    cursor: not-allowed;
                }

                .error-box {
                    color: #ef4444;
                    background-color: #fef2f2;
                    border: 1px solid #fee2e2;
                    padding: 10px 14px;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    text-align: center;
                }

                @media (max-width: 768px) {
                    .address-drawer {
                        width: 100% !important;
                        min-width: unset !important;
                        border-radius: 0;
                    }
                    .address-header {
                        padding: 16px 20px !important;
                    }
                    .address-content {
                        padding: 16px !important;
                    }
                }
                `}
            </style>

            <div className="address-drawer" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="address-header">
                    <div className="address-title-container">
                        <AiOutlineHome size={22} color="#43c8ca" />
                        <h2 className="address-title">
                            {editingAddressId ? 'Edit Address' : showAddForm ? 'Add Address' : 'Addresses'}
                        </h2>
                    </div>
                    <button onClick={onClose} className="address-close-btn" aria-label="Close address drawer">
                        <AiOutlineClose size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="address-content">
                    {!showAddForm ? (
                        <>
                            {/* Saved Addresses list */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                                {filteredAddresses && filteredAddresses.map((addr) => (
                                    <div key={addr._id} className={`address-card ${addr.isDefault ? 'default-address' : ''}`}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <span style={{ fontWeight: '800', fontSize: '14.5px', color: '#1f2937' }}>{addr.name}</span>
                                            <span className="address-badge">{addr.type || 'home'}</span>
                                            {addr.isDefault && (
                                                <span className="address-badge default">Default</span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5', marginBottom: '10px', textAlign: 'left' }}>
                                            {addr.address}, {addr.locality}, {addr.city}, {addr.state} - {addr.pincode}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#1f2937', fontWeight: '700', textAlign: 'left' }}>
                                            Mobile: {addr.shipping_phone}
                                        </div>

                                        <div className="address-actions-footer">
                                            {confirmDeleteId === addr._id ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: '850', color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Remove address?</span>
                                                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                                                        <button
                                                            onClick={() => confirmDeleteAddress(addr._id || '')}
                                                            style={{
                                                                background: '#fef2f2', border: '1px solid #fee2e2', color: '#ef4444', fontSize: '11px',
                                                                fontWeight: '800', cursor: 'pointer', padding: '4px 12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderRadius: '8px'
                                                            }}
                                                        >
                                                            Yes
                                                        </button>
                                                        <button
                                                            onClick={() => setConfirmDeleteId(null)}
                                                            style={{
                                                                background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#4b5563', fontSize: '11px',
                                                                fontWeight: '800', cursor: 'pointer', padding: '4px 12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderRadius: '8px'
                                                            }}
                                                        >
                                                            No
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {!addr.isDefault && (
                                                        <button
                                                            onClick={() => handleSetDefault(addr)}
                                                            className="address-action-link teal"
                                                        >
                                                            Set Default
                                                        </button>
                                                    )}
                                                    {addr.isDefault && (
                                                        <span style={{ fontSize: '11.5px', color: '#43c8ca', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            Default Address
                                                        </span>
                                                    )}

                                                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '16px' }}>
                                                        <button
                                                            onClick={() => handleEditClick(addr)}
                                                            className="address-action-link"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(addr._id)}
                                                            className="address-action-link"
                                                            style={{ color: '#ef4444' }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => {
                                    setEditingAddressId(null);
                                    setName('');
                                    setPhone('');
                                    setPincode('');
                                    setLocality('');
                                    setAddress('');
                                    setCity('');
                                    setState('');
                                    setLandmark('');
                                    setAddressType('Home');
                                    setShowAddForm(true);
                                }}
                                style={{
                                    width: '100%', padding: '16px', border: '1px dashed #43c8ca', background: '#f0fbfb',
                                    color: '#43c8ca', fontWeight: '800', fontSize: '13px', cursor: 'pointer', borderRadius: '12px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    textTransform: 'uppercase', letterSpacing: '1px', transition: 'all 0.2s'
                                }}
                                className="add-new-address-btn"
                            >
                                + Add New Address
                            </button>
                        </>
                    ) : (
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                            {addresses && addresses.length > 0 && (
                                <button
                                    type="button"
                                    onClick={handleBackToSaved}
                                    style={{
                                        background: 'none', border: 'none', color: '#43c8ca', fontWeight: '800', fontSize: '13px',
                                        cursor: 'pointer', padding: 0, textTransform: 'uppercase', letterSpacing: '0.5px',
                                        textAlign: 'left', display: 'block', marginBottom: '4px'
                                    }}
                                >
                                    ← Saved Addresses
                                </button>
                            )}

                            {errorMsg && <div className="error-box">{errorMsg}</div>}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div className="form-group">
                                    <label className="form-label">Contact Name *</label>
                                    <input
                                        className="form-input"
                                        type="text"
                                        required
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        placeholder="e.g. John Doe"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">10-Digit Mobile *</label>
                                    <input
                                        className="form-input"
                                        type="tel"
                                        maxLength={10}
                                        required
                                        value={phone}
                                        onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                                        placeholder="e.g. 9876543210"
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div className="form-group">
                                    <label className="form-label">Pincode *</label>
                                    <input
                                        className="form-input"
                                        type="text"
                                        maxLength={6}
                                        required
                                        value={pincode}
                                        onChange={e => setPincode(e.target.value.replace(/\D/g, ''))}
                                        placeholder="e.g. 201301"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Locality / Area *</label>
                                    <input
                                        className="form-input"
                                        type="text"
                                        required
                                        value={locality}
                                        onChange={e => setLocality(e.target.value)}
                                        placeholder="e.g. Sector 62"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Street Address (House No, Building, Street) (Optional)</label>
                                <input
                                    className="form-input"
                                    type="text"
                                    value={address}
                                    onChange={e => setAddress(e.target.value)}
                                    placeholder="e.g. Flat 402, Block B, Apartments (Optional)"
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div className="form-group">
                                    <label className="form-label">City / District *</label>
                                    <input
                                        className="form-input"
                                        type="text"
                                        required
                                        value={city}
                                        onChange={e => setCity(e.target.value)}
                                        placeholder="e.g. Noida"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">State *</label>
                                    <input
                                        className="form-input"
                                        type="text"
                                        required
                                        value={state}
                                        onChange={e => setState(e.target.value)}
                                        placeholder="e.g. Uttar Pradesh"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Landmark (Optional)</label>
                                <input
                                    className="form-input"
                                    type="text"
                                    value={landmark}
                                    onChange={e => setLandmark(e.target.value)}
                                    placeholder="e.g. Near Fortis Hospital"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Address Type</label>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    {['Home', 'Work', 'Office', 'Other'].map(type => (
                                        <button
                                            key={type}
                                            type="button"
                                            className={`type-btn ${addressType === type ? 'active' : ''}`}
                                            onClick={() => setAddressType(type)}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="primary-action-btn"
                                style={{ marginTop: '12px' }}
                            >
                                {submitting ? 'Saving Address...' : 'Save Address'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddressModal;
